import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import api from '@/api/axios';
import { Button } from '@/components/ui/Button';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';

const { FiDownload, FiX, FiCheckCircle } = FiIcons;

const ExportModal = ({ isOpen, onClose, totalRecords, activeFilters, schemeId }) => {
  const [step, setStep] = useState('confirm');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      setStep('confirm');
      setProgress(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (step === 'processing') {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setStep('ready');
            return 100;
          }
          return prev + (100 / (totalRecords > 1000 ? 50 : 20));
        });
      }, 100);
      return () => clearInterval(interval);
    }
  }, [step, totalRecords]);

  const handleDownload = async () => {
    try {
      // Prepare parameters, removing empty values
      const cleanParams = Object.fromEntries(
        Object.entries({
          scheme_id: schemeId,
          ...activeFilters
        }).filter(([_, v]) => v !== null && v !== undefined && v !== '')
      );

      const res = await api.get('export.php', { 
        params: cleanParams,
        responseType: 'blob' 
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      // Use a timestamped filename consistent with the backend's intent
      a.download = `scheme_${schemeId}_export_${format(new Date(), 'yyyy-MM-dd_HH-mm')}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      onClose();
    } catch (error) {
      console.error('Export Error:', error);
      alert('Export failed. Please check your connection.');
    }
  };

  if (!isOpen) return null;

  const estimatedTime = Math.max(2, Math.ceil(totalRecords / 500));

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-surface-950/60 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[2.5rem] p-8 max-w-md w-full shadow-2xl border border-surface-200"
      >
        <div className="flex justify-between items-start mb-6">
          <div className="w-12 h-12 rounded-2xl bg-primary-50 flex items-center justify-center">
            <SafeIcon icon={FiDownload} className="w-6 h-6 text-primary-600" />
          </div>
          <button onClick={onClose} className="p-2 hover:bg-surface-100 rounded-xl transition-colors">
            <SafeIcon icon={FiX} className="w-5 h-5 text-surface-400" />
          </button>
        </div>

        {step === 'confirm' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-black text-surface-900 leading-tight">Export Scheme Data</h3>
              <p className="text-sm text-surface-500 mt-1 font-medium">Prepare CSV export based on current filters for this scheme.</p>
            </div>

            <div className="bg-surface-50 rounded-2xl p-4 border border-surface-100 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-surface-400 uppercase tracking-wider">Total Records</span>
                <span className="text-sm font-black text-primary-600">{totalRecords.toLocaleString()} Users</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs font-bold text-surface-400 uppercase tracking-wider">Est. Time</span>
                <span className="text-sm font-black text-surface-700">~{estimatedTime}s</span>
              </div>
              
              <div className="pt-2 border-t border-surface-200/60">
                <p className="text-[10px] font-bold text-surface-400 uppercase mb-2">Active Filters</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(activeFilters).map(([k, v]) => v && (
                    <span key={k} className="px-2 py-0.5 bg-white border border-surface-200 rounded-md text-[9px] font-black text-surface-600 uppercase">
                      {k.replace('filter', '')}: {v}
                    </span>
                  ))}
                  {!Object.values(activeFilters).some(v => v) && (
                    <span className="text-[10px] text-surface-400">None (Full Scheme Export)</span>
                  )}
                </div>
              </div>
            </div>

            <Button className="w-full h-12 rounded-2xl" onClick={() => setStep('processing')}>
              Generate CSV File
            </Button>
          </div>
        )}

        {step === 'processing' && (
          <div className="text-center py-6 space-y-6">
            <div className="relative w-24 h-24 mx-auto">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle cx="48" cy="48" r="44" fill="transparent" stroke="#e0e7ff" strokeWidth="4" />
                <circle
                  cx="48"
                  cy="48"
                  r="44"
                  fill="transparent"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeDasharray={276}
                  strokeDashoffset={276 - (276 * progress) / 100}
                  className="text-primary-600 transition-all duration-300"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-lg font-black text-surface-900">{Math.round(progress)}%</span>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-surface-900">Preparing Export...</h3>
              <p className="text-xs text-surface-400 mt-1 italic">Streaming scheme data...</p>
            </div>
          </div>
        )}

        {step === 'ready' && (
          <div className="text-center py-4 space-y-6">
            <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto border-4 border-emerald-100">
              <SafeIcon icon={FiCheckCircle} className="w-10 h-10 text-emerald-500" />
            </div>
            <div>
              <h3 className="text-xl font-black text-surface-900">Export Ready!</h3>
              <p className="text-sm text-surface-500 mt-1 font-medium">{totalRecords} records ready for download.</p>
            </div>
            <Button className="w-full h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700" onClick={handleDownload}>
              <SafeIcon icon={FiDownload} className="w-4 h-4 mr-2" />
              Download CSV Now
            </Button>
            <button onClick={onClose} className="text-xs font-bold text-surface-400 hover:text-surface-600 uppercase tracking-widest">
              Close
            </button>
          </div>
        )}
      </motion.div>
    </div>,
    document.body
  );
};

export default ExportModal;